import { ApiError } from '@/shared/api/client';
import { MAX_RECHARGE_SLOTS, PIX_DUE_DAYS, type PixCharge, type QuotaChannel } from './ledger';

/**
 * Textos da recarga para quem compra: curtos e sem nome de fornecedor.
 * O motivo técnico (Bling, env, token) fica no log e só aparece para o master.
 */

export type ChargeStage = 'preparing' | 'awaiting' | 'unavailable' | 'paid' | 'expired' | 'failed';

/** Sem código PIX depois disto, a tela para de dizer "gerando". */
export const PIX_READY_TIMEOUT_MS = 2 * 60_000;

export const STAGE_COPY: Record<
  ChargeStage,
  { label: string; hint: string; tone: 'secondary' | 'warning' | 'success' | 'destructive' }
> = {
  preparing: { label: 'Gerando PIX', hint: 'O código aparece aqui em instantes.', tone: 'secondary' },
  awaiting: { label: 'Aguardando pagamento', hint: 'Pague no app do seu banco. O saldo entra sozinho.', tone: 'warning' },
  unavailable: { label: 'PIX indisponível', hint: 'Não conseguimos gerar o código. Fale com o suporte.', tone: 'destructive' },
  paid: { label: 'Pago', hint: 'Saldo adicionado.', tone: 'success' },
  expired: { label: 'Expirado', hint: 'Gere um novo PIX para recarregar.', tone: 'secondary' },
  failed: { label: 'Não gerado', hint: 'Tente gerar de novo.', tone: 'destructive' },
};

export function chargeStage(charge: PixCharge, now = Date.now()): ChargeStage {
  if (charge.status === 'paid') return 'paid';
  if (charge.status === 'expired') return 'expired';
  if (charge.status === 'failed') return 'failed';
  if (charge.copiaECola || charge.qrCodeBase64) return 'awaiting';
  const age = now - new Date(charge.createdAt).getTime();
  return Number.isFinite(age) && age > PIX_READY_TIMEOUT_MS ? 'unavailable' : 'preparing';
}

/** Link (página do PIX), imagem `data:` ou nada. */
export function pixAsset(charge: PixCharge): { link?: string; image?: string } {
  const value = charge.qrCodeBase64?.trim();
  if (!value) return {};
  if (value.startsWith('data:')) return { image: value };
  if (value.startsWith('https://') || value.startsWith('http://')) return { link: value };
  return {};
}

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

export function formatCents(cents: number): string {
  return brl.format(cents / 100);
}

export function unitLabel(channel: QuotaChannel, amount: number): string {
  if (channel === 'sms') return 'SMS';
  return amount === 1 ? 'e-mail' : 'e-mails';
}

export function chargeTitle(charge: Pick<PixCharge, 'channel' | 'slots'>): string {
  return `${charge.slots.toLocaleString('pt-BR')} ${unitLabel(charge.channel, charge.slots)}`;
}

export function unitPriceLabel(channel: QuotaChannel, cents: number): string {
  return `${formatCents(cents)} por ${unitLabel(channel, 1)}`;
}

/** Vencimento do PIX (o BFF manda D+`PIX_DUE_DAYS` em data UTC para o Bling). */
export function chargeDueLabel(createdAt: string): string {
  const due = new Date(createdAt);
  if (Number.isNaN(due.getTime())) return '';
  due.setUTCDate(due.getUTCDate() + PIX_DUE_DAYS);
  return `Vence em ${due.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'UTC' })}`;
}

/** Quantidade digitada → slots válidos ou o erro para mostrar. */
export function parseRechargeAmount(raw: string): { slots: number } | { error: string } {
  const slots = Math.floor(Number(raw));
  if (!raw || !Number.isFinite(slots) || slots < 1) return { error: RECHARGE_ERRORS.invalid };
  if (slots > MAX_RECHARGE_SLOTS) return { error: RECHARGE_ERRORS.tooMany };
  return { slots };
}

export function chargeWhen(iso: string, now = new Date()): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const time = at.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const sameDay = at.toDateString() === now.toDateString();
  return sameDay ? `Hoje, ${time}` : `${at.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}, ${time}`;
}

const UNAVAILABLE_CODES = new Set([
  'marketing_bff_not_configured',
  'marketing_bff_key_rejected',
  'bling_not_authorized',
  'bling_not_configured',
  'bling_contact_not_configured',
  'bling_portador_not_configured',
  'bling_price_not_configured',
]);

export const RECHARGE_ERRORS = {
  unavailable: 'Recarga indisponível no momento. Tente mais tarde.',
  retry: 'Não foi possível gerar o PIX. Tente de novo em alguns minutos.',
  invalid: 'Informe uma quantidade válida.',
  tooMany: `O máximo por recarga é ${MAX_RECHARGE_SLOTS.toLocaleString('pt-BR')}.`,
  forbidden: 'Você não tem acesso a esta loja.',
  session: 'Sua sessão expirou. Entre de novo.',
} as const;

/** Erro da geração do PIX → frase para quem compra. */
export function rechargeErrorText(input: { status?: number; code?: string }): string {
  if (input.code && UNAVAILABLE_CODES.has(input.code)) return RECHARGE_ERRORS.unavailable;
  if (input.status === 401) return RECHARGE_ERRORS.session;
  if (input.status === 403) return RECHARGE_ERRORS.forbidden;
  if (input.status === 400) return RECHARGE_ERRORS.invalid;
  if (input.status === 503) return RECHARGE_ERRORS.unavailable;
  return RECHARGE_ERRORS.retry;
}

/** Código e status de um erro do `http` (o corpo do BFF traz `code`). */
export function rechargeErrorOf(err: unknown): { status?: number; code?: string; detail?: string } {
  if (err instanceof ApiError) {
    const data = err.data as { code?: unknown } | undefined;
    return { status: err.status, code: typeof data?.code === 'string' ? data.code : undefined, detail: err.message };
  }
  return { detail: err instanceof Error ? err.message : undefined };
}
